
import { useEffect, useState } from 'react';
import { ProductLayout } from './ProductLayout';
import { ProductGallery } from './ProductGallery';
import { ProductInfo } from './ProductInfo';

import { ProductSkeleton } from './ProductSkeleton';

interface ProductPageProps {
    productId: number;
    initialData?: any; // Allow injecting data (e.g. for playground)
}

export const ProductPage = ({ productId, initialData }: ProductPageProps) => {
    const [product, setProduct] = useState<any>(initialData || null);
    const [loading, setLoading] = useState(!initialData);

    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!product && productId) {
            setLoading(true);
            const apiUrl = `/wp-json/evcs/v1/products/${productId}`;

            fetch(apiUrl)
                .then(res => {
                    if (!res.ok) throw new Error('Erreur chargement produit');
                    return res.json();
                })
                .then(data => {
                    // Combine featured image and gallery
                    const images: Array<{ id: number; src: string; fullSrc: string; alt: string }> = [];
                    if (data.featured_image && data.featured_image.url) {
                        images.push({
                            id: data.featured_image.id || 0,
                            src: data.featured_image.url,
                            fullSrc: data.featured_image.full_url || data.featured_image.url,
                            alt: data.featured_image.alt || data.name
                        });
                    }
                    if (data.gallery && Array.isArray(data.gallery)) {
                        data.gallery.forEach((img: any, index: number) => {
                            const url = typeof img === 'string' ? img : img.url;
                            const fullUrl = typeof img === 'string' ? img : (img.full_url || img.url);
                            const imgId = typeof img === 'string' ? index + 100 : img.id;
                            const imgAlt = typeof img === 'string' ? `${data.name} - ${index + 1}` : (img.alt || `${data.name} - ${index + 1}`);

                            if (url !== data.featured_image?.url) {
                                images.push({
                                    id: imgId,
                                    src: url,
                                    fullSrc: fullUrl,
                                    alt: imgAlt
                                });
                            }
                        });
                    }

                    setProduct({
                        id: data.id,
                        name: data.name,
                        price: data.price,
                        shortDescription: data.short_description,
                        stock: data.stock_qty,
                        inStock: data.in_stock,
                        images: images,
                        next_product: data.next_product,
                        previous_product: data.previous_product,
                        tabs: data.tabs,
                        imagePositions: data.image_positions || {},
                        broderie_options: data.broderie_options || [],
                    });
                    setLoading(false);
                })
                .catch(err => {
                    console.error(err);
                    setError('Impossible de charger le produit.');
                    setLoading(false);
                });
        }
    }, [productId, product]);

    if (error) return <div>{error}</div>;

    if (!product && loading) {
        return <ProductSkeleton />;
    }

    if (!product) {
        return <div>Produit introuvable</div>;
    }

    return (
        <ProductLayout
            gallery={<ProductGallery images={product.images} imagePositions={product.imagePositions} />}
            info={<ProductInfo product={product} />}
        />
    );
};
